import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { api } from '../../utils/apiClient';
import './AchievementsPanel.css';

interface AchievementCondition {
  id?: string;
  conditionType: string;
  conditionValue: number;
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string | null;
  points: number;
  restaurantId: string | null;
  conditionRules: AchievementCondition[];
}

interface AchievementModalProps {
  restaurantId: string | null;
  achievement: Achievement | null;
  onClose: () => void;
  onSuccess: () => void;
}

function AchievementModal({ restaurantId, achievement, onClose, onSuccess }: AchievementModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    points: 0,
    conditionType: 'totalOrders' as string,
    conditionValue: 10,
    conditionDays: 7,
    conditionTag: '',
    conditionProductId: '',
    conditionProductIds: [] as string[],
  });
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [restaurantProducts, setRestaurantProducts] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [iconUploading, setIconUploading] = useState(false);
  const iconFileRef = useRef<HTMLInputElement>(null);

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIconUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post<{ url: string }>('/api/upload?folder=achievements', form);
      setFormData(prev => ({ ...prev, icon: res.data.url }));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Upload failed');
    } finally {
      setIconUploading(false);
      if (iconFileRef.current) iconFileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (achievement) {
      const firstCondition = achievement.conditionRules?.[0] as { conditionType: string; conditionValue: number; parameters?: { days?: number; tag?: string; productId?: string; productIds?: string[] } } | undefined;
      setFormData({
        name: achievement.name,
        description: achievement.description,
        icon: achievement.icon || '',
        points: achievement.points,
        conditionType: firstCondition?.conditionType || 'totalOrders',
        conditionValue: firstCondition ? Number(firstCondition.conditionValue) : 10,
        conditionDays: firstCondition?.parameters?.days ?? 7,
        conditionTag: firstCondition?.parameters?.tag ?? '',
        conditionProductId: firstCondition?.parameters?.productId ?? '',
        conditionProductIds: firstCondition?.parameters?.productIds ?? [],
      });
    }
  }, [achievement]);

  useEffect(() => {
    axios.get(`${API_URL}/api/products/suggest-tags`).then((r) => {
      setAvailableTags(r.data?.allTags || []);
    }).catch(() => {});
    if (restaurantId) {
      axios.get(`${API_URL}/api/products/restaurant/${restaurantId}`).then((r) => {
        const list = (r.data || []).map((p: any) => ({ id: p.id, name: p.name }));
        setRestaurantProducts(list);
      }).catch(() => {});
    }
  }, [restaurantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      icon: formData.icon.trim() || null,
      points: parseInt(String(formData.points)) || 0,
      restaurantId: restaurantId || undefined,
    };

    try {
      if (achievement) {
        await axios.patch(`${API_URL}/api/achievements/${achievement.id}`, {
          name: payload.name,
          description: payload.description,
          icon: payload.icon,
          points: payload.points,
        });
        // Add condition only if none exists (create flow); conditions can't be updated via API yet
        const hasCondition = achievement.conditionRules?.length;
        if (!hasCondition) {
          const condPayload: { conditionType: string; conditionValue: number; parameters?: { days?: number; tag?: string } } = {
            conditionType: formData.conditionType,
            conditionValue: formData.conditionType === 'totalSpent' || formData.conditionType === 'avgOrderValue'
              ? parseFloat(String(formData.conditionValue)) || 0
              : parseInt(String(formData.conditionValue)) || 0,
          };
          if (formData.conditionType === 'ordersInPeriod') condPayload.parameters = { days: formData.conditionDays } as any;
          if (formData.conditionType === 'tagOrderCount' && formData.conditionTag.trim()) condPayload.parameters = { tag: formData.conditionTag.trim().toLowerCase() } as any;
          if (formData.conditionType === 'productCount' && formData.conditionProductId) condPayload.parameters = { productId: formData.conditionProductId } as any;
          if (formData.conditionType === 'comboOrderCount' && formData.conditionProductIds.length > 0) condPayload.parameters = { productIds: formData.conditionProductIds } as any;
          if (formData.conditionType === 'uniqueProducts' && formData.conditionProductIds.length > 0) condPayload.parameters = { productIds: formData.conditionProductIds } as any;
          await axios.post(`${API_URL}/api/achievements/${achievement.id}/conditions`, condPayload);
        }
      } else {
        const created = await axios.post(`${API_URL}/api/achievements`, payload);
        const condPayload: { conditionType: string; conditionValue: number; parameters?: { days?: number; tag?: string } } = {
          conditionType: formData.conditionType,
          conditionValue: formData.conditionType === 'totalSpent' || formData.conditionType === 'avgOrderValue'
            ? parseFloat(String(formData.conditionValue)) || 0
            : parseInt(String(formData.conditionValue)) || 0,
        };
        if (formData.conditionType === 'ordersInPeriod') condPayload.parameters = { days: formData.conditionDays };
        if (formData.conditionType === 'tagOrderCount' && formData.conditionTag.trim()) condPayload.parameters = { tag: formData.conditionTag.trim().toLowerCase() };
        await axios.post(`${API_URL}/api/achievements/${created.data.id}/conditions`, condPayload);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'modals.failedSaveAchievement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content product-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{achievement ? t('modals.editAchievement') : t('modals.createAchievement')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-box">{error.startsWith('modals.') ? t(error) : error}</div>}

            <div className="form-group">
              <label>{t('modals.achievementName')}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., First Order"
              />
            </div>

            <div className="form-group">
              <label>{t('modals.achievementDesc')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder={t('modals.achievementDescPlaceholder')}
                rows={2}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('modals.points')}</label>
                <input
                  type="number"
                  min="0"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                  placeholder="10"
                />
                <small>{t('modals.pointsHint')}</small>
              </div>

              <div className="form-group">
                <label>{t('modals.iconEmoji', 'Иконка (emoji или GIF)')}</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Preview */}
                  <div style={{ width: 56, height: 56, borderRadius: 10, border: '1px solid rgba(180,150,120,0.18)', background: 'var(--a-warm-page)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0, overflow: 'hidden' }}>
                    {formData.icon?.startsWith('http')
                      ? <img src={formData.icon} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      : <span>{formData.icon || ''}</span>
                    }
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* File upload */}
                    <button
                      type="button"
                      style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid var(--admin-border, rgba(180,150,120,0.18))', background: 'var(--a-warm-page)', color: 'var(--admin-text, var(--a-dark))', fontSize: 13, cursor: iconUploading ? 'wait' : 'pointer' }}
                      onClick={() => iconFileRef.current?.click()}
                      disabled={iconUploading}
                    >
                      {iconUploading ? t('modals.uploading', 'Загружаем...') : t('modals.uploadGif', 'Загрузить GIF / изображение')}
                    </button>
                    <input ref={iconFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleIconUpload} />
                    {/* Emoji fallback */}
                    <input
                      type="text"
                      placeholder="Или введите emoji"
                      value={formData.icon?.startsWith('http') ? '' : (formData.icon || '')}
                      disabled={formData.icon?.startsWith('http')}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid var(--admin-border, rgba(180,150,120,0.18))', background: 'var(--a-white)', color: 'var(--admin-text, var(--a-dark))', fontSize: 13, opacity: formData.icon?.startsWith('http') ? 0.4 : 1 }}
                    />
                    {formData.icon?.startsWith('http') && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, icon: '' })}
                        style={{ fontSize: 12, color: 'var(--a-red-lighter)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
                      >
                        Удалить изображение
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="achievement-condition-section">
              <label>{t('modals.unlockCondition')}</label>
              <div className="form-row">
                <div className="form-group">
                  <select
                    value={formData.conditionType}
                    onChange={(e) => setFormData({ ...formData, conditionType: e.target.value })}
                  >
                    <option value="totalOrders">{t('modals.totalOrders')}</option>
                    <option value="totalSpent">{t('modals.totalSpent')}</option>
                    <option value="loyaltyPoints">Loyalty points</option>
                    <option value="ordersInPeriod">Orders in period</option>
                    <option value="uniqueRestaurants">Unique restaurants</option>
                    <option value="upsellCount">Upsells accepted</option>
                    <option value="avgOrderValue">Average order value</option>
                    <option value="tagOrderCount">Tag-based (e.g. chocolate x3)</option>
                    <option value="productCount">Specific product (sum of qty)</option>
                    <option value="uniqueProducts">Unique products tasted</option>
                    <option value="comboOrderCount">Combo (these products in one order)</option>
                    <option value="consecutiveDays">Consecutive days streak</option>
                  </select>
                </div>
                <div className="form-group">
                  <input
                    type="number"
                    min="0"
                    step={formData.conditionType === 'totalSpent' || formData.conditionType === 'avgOrderValue' ? 0.01 : 1}
                    value={formData.conditionValue}
                    onChange={(e) => setFormData({ ...formData, conditionValue: parseFloat(e.target.value) || 0 })}
                    placeholder={
                      formData.conditionType === 'totalSpent' ? '100' :
                      formData.conditionType === 'avgOrderValue' ? '25' :
                      formData.conditionType === 'ordersInPeriod' ? '5' : '10'
                    }
                  />
                </div>
                {formData.conditionType === 'ordersInPeriod' && (
                  <div className="form-group">
                    <label>Days</label>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={formData.conditionDays}
                      onChange={(e) => setFormData({ ...formData, conditionDays: parseInt(e.target.value) || 7 })}
                    />
                  </div>
                )}
                {formData.conditionType === 'tagOrderCount' && (
                  <div className="form-group">
                    <label>Product tag</label>
                    <select
                      value={formData.conditionTag}
                      onChange={(e) => setFormData({ ...formData, conditionTag: e.target.value })}
                      required
                    >
                      <option value="">Select tag...</option>
                      {availableTags.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                )}
                {formData.conditionType === 'productCount' && (
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <label>Product</label>
                    <select
                      value={formData.conditionProductId}
                      onChange={(e) => setFormData({ ...formData, conditionProductId: e.target.value })}
                      required
                    >
                      <option value="">Select product...</option>
                      {restaurantProducts.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {formData.conditionType === 'uniqueProducts' && restaurantProducts.length > 0 && (
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <label>Whitelist (optional — leave empty to count any distinct product)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {restaurantProducts.map((p) => {
                        const active = formData.conditionProductIds.includes(p.id);
                        return (
                          <button
                            type="button"
                            key={p.id}
                            className={`tag-btn ${active ? 'active' : ''}`}
                            onClick={() => setFormData((prev) => ({
                              ...prev,
                              conditionProductIds: active
                                ? prev.conditionProductIds.filter((x) => x !== p.id)
                                : [...prev.conditionProductIds, p.id],
                            }))}
                          >
                            {active ? '✓ ' : '+ '}{p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {formData.conditionType === 'comboOrderCount' && (
                  <div className="form-group" style={{ flex: '1 1 100%' }}>
                    <label>Combo products (must appear together in same order)</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {restaurantProducts.map((p) => {
                        const active = formData.conditionProductIds.includes(p.id);
                        return (
                          <button
                            type="button"
                            key={p.id}
                            className={`tag-btn ${active ? 'active' : ''}`}
                            onClick={() => setFormData((prev) => ({
                              ...prev,
                              conditionProductIds: active
                                ? prev.conditionProductIds.filter((x) => x !== p.id)
                                : [...prev.conditionProductIds, p.id],
                            }))}
                          >
                            {active ? '✓ ' : '+ '}{p.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <small className="condition-hint">
                {formData.conditionType === 'totalSpent' && t('modals.totalSpentHint')}
                {formData.conditionType === 'totalOrders' && t('modals.totalOrdersHint')}
                {formData.conditionType === 'loyaltyPoints' && 'Customer must reach this many loyalty points'}
                {formData.conditionType === 'ordersInPeriod' && 'Place X orders in the last N days'}
                {formData.conditionType === 'uniqueRestaurants' && 'Order from this many different restaurants'}
                {formData.conditionType === 'upsellCount' && 'Accept this many upsell recommendations'}
                {formData.conditionType === 'avgOrderValue' && 'Average order value must be at least this'}
                {formData.conditionType === 'tagOrderCount' && 'Order items with this tag X times (e.g. Chocolate Hero)'}
                {formData.conditionType === 'productCount' && 'Order this specific product X times in total (sum of quantities)'}
                {formData.conditionType === 'uniqueProducts' && 'Try this many distinct products at least once'}
                {formData.conditionType === 'comboOrderCount' && 'How many times all selected products must appear together in one order'}
                {formData.conditionType === 'consecutiveDays' && 'Place at least one order on this many consecutive days'}
              </small>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-cancel">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? t('modals.saving') : achievement ? t('modals.update') : t('modals.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AchievementModal;
